import { ToggleLegend } from '@/components/ECharts';
import { NumberFormatter } from '@/context/configContext';
import { BaseChart } from '@/dashboards/TitleDashboard/components/BaseChart';
import { CustomTooltip } from '@/dashboards/TitleDashboard/components/CustomTooltip';
import { GradientLegend } from '@/dashboards/TitleDashboard/components/GradientLegend';
import {
    Colors,
    pickColors,
} from '@/dashboards/TitleDashboard/helpers/pickColors';
import {
    EChartsOption,
    GetModelVsActualData,
    LegendClickEvent,
} from '@/dashboards/TitleDashboard/types';
import { twCn } from '@/utils';
import { getColorFromRange } from '@/utils/colorUtils';
import { jsxToHtml } from '@/utils/reactUtils';
import EChartsReact from 'echarts-for-react';
import { Opts } from 'echarts-for-react/lib/types';
import {
    FC,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import {
    useDashboardGlobalContext
} from '@/dashboards/providers/dashboardGlobalContext';

const getEChartsOptions = ({
    data,
    residual: residualConfig,
    formatNumber,
}: {
    formatNumber: NumberFormatter;
    data: GetModelVsActualData | undefined;
    residual: Omit<ModelVsActualGraphProps['residual'], 'colors'> & {
        colors: {
            min: string;
            max: string;
        };
    };
}): EChartsOption => {
    if (!data) {
        return {
            legend: {
                show: true,
                showCustom: true,
                orient: 'vertical',
                align: 'left',
                icon: 'circle',
                data: ['Model', 'Actual'],
                left: 100,
            },
        };
    }

    return {
        legend: {
            show: true,
            showCustom: true,
            orient: 'vertical',
            align: 'left',
            icon: 'circle',
            data: ['Model', 'Actual'],
            left: 100,
        },
        tooltip: [
            {
                trigger: 'axis',
                triggerOn: 'mousemove|click',
                backgroundColor: 'transparent',
                borderWidth: 0,
                padding: 0,
                formatter: (params: any) => {
                    return jsxToHtml(
                        <CustomTooltip
                            params={params}
                            formatNumber={formatNumber}
                        />,
                    );
                },
            },
        ],
        xAxis: [
            {
                show: true,
                data: data.modelData.map((d) => d.period),
                type: 'category',
                splitLine: { show: false },
                tooltip: {
                    show: false,
                },
                axisPointer: {
                    type: 'shadow',
                    z: 100,
                },
                axisLabel: {
                    fontSize: 14,
                    fontWeight: 'lighter',
                    margin: 20,
                },
            },
        ],
        yAxis: [
            {
                type: 'value',
                axisLabel: {
                    formatter: (value) => {
                        return formatNumber(value, {
                            locale: 'en-US',
                            notation: 'compact',
                            maximumFractionDigits: 3,
                        });
                    },
                    fontSize: 16,
                    fontWeight: 'lighter',
                },
                axisTick: {
                    show: true,
                },
            },
        ],
        series: [
            {
                name: 'Model',
                type: 'line',
                yAxisIndex: 0,
                itemStyle: {
                    color: '#73EFEA',
                },
                lineStyle: {
                    width: 2,
                },
                showAllSymbol: true,
                data: data.modelData.map((d) => d.kpi),
                smooth: false,
                symbolSize: 4,
            },
            {
                name: 'Actual',
                type: 'line',
                yAxisIndex: 0,
                showAllSymbol: true,
                itemStyle: {
                    color: '#2B67FF',
                },
                lineStyle: {
                    width: 2,
                },
                smooth: false,
                data: data.actualData.map((d) => d.kpi),
                symbolSize: 4,
            },
            {
                name: 'Residual',
                type: 'bar',
                stack: 'residual',
                tooltip: {
                    valueFormatter: function (value: any) {
                        return formatNumber(value);
                    },
                },
                data: data.modelData.map(({ residual }) => {
                    return {
                        value: residual,
                        itemStyle: {
                            color: getColorFromRange(residual, {
                                color: {
                                    start: residualConfig.colors.min,
                                    end: residualConfig.colors.max,
                                    steps: 50,
                                },
                                rule: {
                                    min: residualConfig.minResidual || 0,
                                    max: residualConfig.maxResidual || 0,
                                },
                            }),
                        },
                    };
                }),
            },
        ],
    };
};

type ToggleLegend = {
    type: 'TOGGLE_LEGEND';
    payload: { name: string; value?: boolean };
};

type SetData = {
    type: 'SET_DATA';
    payload: Partial<GraphState>;
};

type GraphState = {
    legend: { name: string; selected: boolean; color?: string }[];
};

const graphReducer = (state: GraphState, action: ToggleLegend | SetData) => {
    switch (action.type) {
        case 'TOGGLE_LEGEND':
            return {
                ...state,
                legend: state.legend.map((item) => {
                    if (item.name === action.payload.name) {
                        return {
                            ...item,
                            selected:
                                action.payload.value !== undefined
                                    ? action.payload.value
                                    : !item.selected,
                        };
                    }
                    return item;
                }),
            };
        case 'SET_DATA':
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
    }
};

function getLegendItems(chartsOption: EChartsOption) {
    const { legend, series } = chartsOption;

    const legendData: {
        name: string;
        selected: boolean;
        color?: string;
    }[] = [];

    if (legend && Array.isArray(series)) {
        if (legend.show && legend.data)
            legend.data.forEach((name) => {
                const parsedName = typeof name === 'string' ? name : name.name;
                const foundSeries = series.find((s) => s.name === parsedName);
                const color = (foundSeries as unknown as any)?.itemStyle?.color;
                if (parsedName) {
                    legendData.push({
                        name: parsedName,
                        selected: true,
                        color: typeof color === 'string' ? color : undefined,
                    });
                }
            });
    }

    return legendData;
}

const useChart = ({ chartOptions }: { chartOptions: EChartsOption }) => {
    const chartRef = useRef<EChartsReact>(null);

    const [{ legend }, dispatch] = useReducer(graphReducer, {
        legend: [],
    });

    const toggleLegend = useCallback((name: string) => {
        chartRef.current?.getEchartsInstance()?.dispatchAction({
            type: 'legendToggleSelect',
            name,
        });
    }, []);

    const onLegendChange = useCallback((params: LegendClickEvent) => {
        Object.entries(params.selected).forEach(([name, value]) => {
            dispatch({
                type: 'TOGGLE_LEGEND',
                payload: {
                    name,
                    value,
                },
            });
        });
    }, []);

    useEffect(() => {
        dispatch({
            type: 'SET_DATA',
            payload: {
                legend: getLegendItems(chartOptions),
            },
        });
    }, [chartOptions]);

    useEffect(() => {
        const listener = () => {
            const eCharts = chartRef.current?.getEchartsInstance();
            eCharts?.resize();
        };

        window.addEventListener('resize', listener);

        return () => window.removeEventListener('resize', listener);
    });

    const combinedChartOptions = useMemo(() => {
        return {
            ...chartOptions,
            legend: {
                ...chartOptions.legend,
                show: chartOptions.legend.showCustom
                    ? false
                    : chartOptions.legend.show,
            },
        };
    }, [chartOptions]);

    return {
        ref: chartRef,
        legendItems: legend,
        toggleLegend,
        chartOptions: combinedChartOptions,
        onLegendChange,
    };
};

interface ModelVsActualGraphProps<
    T extends GetModelVsActualData | undefined =
        | GetModelVsActualData
        | undefined,
> {
    isLoading?: boolean;
    className?: string;
    residual: {
        maxResidual: number | undefined;
        minResidual: number | undefined;
        colors: Colors;
    };

    data: T;
}

export const ModelVsActualGraph: FC<ModelVsActualGraphProps> = memo(
    ({ className, isLoading, data, residual }) => {
        const {
            contributionFormatter,
        } = useDashboardGlobalContext();

        const colors = useMemo(() => {
            return pickColors({
                minValue: residual.minResidual,
                maxValue: residual.maxResidual,
                colors: residual.colors,
            });
        }, [residual]);

        const eChartsOptions = useMemo(() => {
            return getEChartsOptions({
                data,
                formatNumber: contributionFormatter,
                residual: {
                    ...residual,
                    colors,
                },
            });
        }, [colors, data, residual, contributionFormatter]);

        const {
            ref: chartRef,
            legendItems,
            toggleLegend,
            chartOptions,
            onLegendChange,
        } = useChart({
            chartOptions: eChartsOptions,
        });

        const opts = useMemo<Opts>(() => ({ renderer: 'svg' }), []);

        if (isLoading) {
            return null;
        }

        return (
            <div className={twCn('relative w-full', className)}>
                <div className="absolute z-10 right-4 top-0 flex flex-row gap-4 align-center justify-center align-middle">
                    <ToggleLegend
                        legendItems={legendItems}
                        onSelect={toggleLegend}
                    />
                    <GradientLegend
                        title="Residual"
                        tooltip="A difference between the actual KPI and the KPI predicted by the model. Closer to zero is better."
                        minValue={contributionFormatter(
                            Math.round(residual.minResidual || 0),
                        )}
                        maxValue={contributionFormatter(
                            Math.round(residual.maxResidual || 0),
                        )}
                        gradient={{
                            minColor: colors.onlyNegative
                                ? colors.max
                                : colors.min,
                            maxColor: colors.onlyNegative
                                ? colors.min
                                : colors.max,
                        }}
                    />
                </div>
                <BaseChart
                    $shouldExpand
                    ref={chartRef}
                    option={chartOptions}
                    onLegendChange={onLegendChange}
                    opts={opts}
                    theme={{
                        line: {
                            itemStyle: {
                                borderWidth: '4',
                            },
                            lineStyle: {
                                width: '3',
                            },
                            symbolSize: '0',
                            symbol: 'circle',
                            smooth: true,
                        },
                    }}
                />
            </div>
        );
    },
);

ModelVsActualGraph.displayName = 'ModelVsActualGraph';
